/**
 * @file AdditiveMultiPicker.js
 * @version 1.2.3
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-12
 * @modified 2026-09-30
 */

import React, { useEffect, useState, useRef } from 'react';
import MultiSelectDropdown from './MultiSelectDropdown';
import { additiveLabel } from '../utils/additiveLabel';

/**
 * Multi-select picker for additives, scoped to a single supplier.
 *
 * Props:
 *   supplierAccountId: number | null  - When null the picker is disabled and
 *                                       shows "Choose a supplier first".
 *   value: Array<additive>            - Currently-selected additives. Each item
 *                                       is { additive_id, additive_code,
 *                                       additive_name, additive_price }.
 *   onChange: (newValue) => void      - Receives the new array of additive
 *                                       objects (full snapshot, not just ids).
 *
 * Fetches `/api/additives?supplier_account_id={N}` on supplier change. Results
 * are cached per supplier within the component's lifetime so re-opening a line
 * doesn't refetch.
 */
// `placeholder` overrides the computed trigger text.
function AdditiveMultiPicker({
  supplierAccountId, value = [], onChange, disabled = false, placeholder: placeholderOverride,
  // Passed straight through to MultiSelectDropdown. The order-lines grid needs
  // all three: its cells are short and it scrolls horizontally, so without them
  // the menu measures against a few pixels and opens clipped.
  hideTags = false, escapeOverflow = false, reflowOnScroll = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!supplierAccountId) {
      setOptions([]);
      return undefined;
    }
    const cached = cacheRef.current.get(supplierAccountId);
    if (cached) {
      setOptions(cached);
      return undefined;
    }
    setLoading(true);
    fetch(`/api/additives?supplier_account_id=${supplierAccountId}&limit=500`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const rows = Array.isArray(json?.data) ? json.data : [];
        cacheRef.current.set(supplierAccountId, rows);
        setOptions(rows);
      })
      .catch(err => {
        console.error('AdditiveMultiPicker fetch error:', err);
        if (!cancelled) setOptions([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [supplierAccountId]);

  // Build the MultiSelectDropdown's options + value (id-keyed).
  const optionMap = new Map(options.map(o => [o.id, o]));
  const selectedIds = (value || []).map(v => v.additive_id ?? v.additiveId ?? v.id).filter(Boolean);

  // Name first, code in brackets after it, the code dropped when it says the
  // same thing as the name — the one rule, in utils/additiveLabel.
  const optionLabel = (code, name) => additiveLabel({ additive_code: code, additive_name: name });

  const dropdownOptions = options.map(o => ({
    value: o.id,
    label: optionLabel(o.code, o.name),
    searchText: `${o.code || ''} ${o.name || ''}`,
  }));

  // If the current value contains additives that aren't in the dropdown options
  // (e.g. the supplier changed but the selection is stale), surface them so
  // they stay visible until the caller clears them.
  for (const v of value || []) {
    const id = v.additive_id ?? v.additiveId ?? v.id;
    if (id && !optionMap.has(id)) {
      const code = v.additive_code || v.additiveCode || '';
      const name = v.additive_name || v.additiveName || '';
      dropdownOptions.push({ value: id, label: optionLabel(code, name), searchText: `${code} ${name}`.trim() });
    }
  }

  const handleChange = (newIds) => {
    const next = newIds.map(id => {
      const opt = optionMap.get(id);
      if (opt) {
        return {
          additive_id: opt.id,
          additive_code: opt.code,
          additive_name: opt.name,
          additive_price: opt.price,
        };
      }
      const existing = (value || []).find(v => (v.additive_id ?? v.additiveId ?? v.id) === id);
      return existing || null;
    }).filter(Boolean);
    onChange(next);
  };

  const placeholder = placeholderOverride
    || (!supplierAccountId
      ? 'Choose a supplier first'
      : (loading ? 'Loading…' : 'Select additives…'));

  // When no supplier is set, render a disabled trigger that mimics the
  // MultiSelectDropdown look without firing fetches.
  if (!supplierAccountId || disabled) {
    return (
      <div className="multi-select-dropdown">
        <button className="dropdown-trigger" type="button" disabled>
          <span className="trigger-text">{placeholder}</span>
          <span className="material-symbols-rounded dropdown-arrow">expand_more</span>
        </button>
      </div>
    );
  }

  return (
    <MultiSelectDropdown
      options={dropdownOptions}
      value={selectedIds}
      onChange={handleChange}
      placeholder={placeholder}
      emptyMessage={loading ? 'Loading…' : 'No additives for this supplier'}
      hideTags={hideTags}
      escapeOverflow={escapeOverflow}
      reflowOnScroll={reflowOnScroll}
    />
  );
}

export default AdditiveMultiPicker;
